import React from 'react';
import {
    BookOpen,
    Users,
    Calendar,
    Award,
    Clock,
    FileText,
    TrendingUp,
    Target,
    CheckCircle,
    AlertCircle,
    Star,
    GraduationCap,
    Activity
} from 'lucide-react';
import { Card } from '../../components/ui';

const DashboardTeacher: React.FC = () => {
    const teacherStats = [
        {
            name: 'My Classes',
            value: '4',
            change: '+1',
            changeType: 'positive',
            icon: BookOpen,
            color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
            trend: 'up',
        },
        {
            name: 'Total Students',
            value: '120',
            change: '+8',
            changeType: 'positive',
            icon: Users,
            color: 'bg-gradient-to-r from-teal-500 to-teal-600',
            trend: 'up',
        },
        {
            name: 'Assignments Due',
            value: '12',
            change: '-3',
            changeType: 'negative',
            icon: FileText,
            color: 'bg-gradient-to-r from-orange-500 to-orange-600',
            trend: 'down',
        },
        {
            name: 'Attendance Rate',
            value: '94%',
            change: '+2%',
            changeType: 'positive',
            icon: Award,
            color: 'bg-gradient-to-r from-cyan-500 to-cyan-600',
            trend: 'up',
        },
    ];

    const teacherMetrics = [
        {
            name: 'Hours This Week',
            value: '32',
            change: '+4',
            changeType: 'positive',
            icon: Clock,
            trend: 'up',
        },
        {
            name: 'Graded Papers',
            value: '45',
            change: '+12',
            changeType: 'positive',
            icon: CheckCircle,
            trend: 'up',
        },
        {
            name: 'Upcoming Exams',
            value: '3',
            change: 'Next week',
            changeType: 'neutral',
            icon: Target,
            trend: 'neutral',
        },
    ];

    const myClasses = [
        { subject: 'Mathematics', grade: 'Grade 10A', students: 30, nextClass: 'Today 2:00 PM' },
        { subject: 'Mathematics', grade: 'Grade 10B', students: 28, nextClass: 'Today 3:30 PM' },
        { subject: 'Advanced Math', grade: 'Grade 11', students: 25, nextClass: 'Tomorrow 10:00 AM' },
        { subject: 'Calculus', grade: 'Grade 12', students: 22, nextClass: 'Tomorrow 11:30 AM' },
    ];

    const recentActivities = [
        {
            id: 1,
            type: 'assignment',
            message: 'Graded 15 assignments for Grade 10A',
            time: '1 hour ago',
        },
        {
            id: 2,
            type: 'attendance',
            message: 'Marked attendance for Mathematics class',
            time: '3 hours ago',
        },
        {
            id: 3,
            type: 'exam',
            message: 'Scheduled mid-term exam for Grade 11',
            time: '1 day ago',
        },
        {
            id: 4,
            type: 'meeting',
            message: 'Attended department meeting',
            time: '2 days ago',
        },
    ];

    const upcomingTasks = [
        { task: 'Prepare lesson plan for Grade 12', due: 'Today', priority: 'high' },
        { task: 'Grade homework assignments', due: 'Tomorrow', priority: 'medium' },
        { task: 'Parent-teacher meeting', due: 'Friday', priority: 'high' },
        { task: 'Update student progress reports', due: 'Next week', priority: 'low' },
    ];

    const studentPerformance = [
        { grade: 'Grade 10A', average: 85, improvement: '+5%', color: 'bg-emerald-500' },
        { grade: 'Grade 10B', average: 82, improvement: '+3%', color: 'bg-teal-500' },
        { grade: 'Grade 11', average: 88, improvement: '+7%', color: 'bg-cyan-500' },
        { grade: 'Grade 12', average: 90, improvement: '+4%', color: 'bg-orange-500' },
    ];

    return (
        <div className="space-y-6">
            {/* Teacher Dashboard Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold gradient-text">Teacher Dashboard</h2>
                    <p className="text-gray-600 text-sm">Teaching Management Overview</p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <BookOpen className="h-3 w-3" />
                    <span>Teacher Portal</span>
                </div>
            </div>

            {/* Teacher Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {teacherStats.map((stat) => (
                    <Card key={stat.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                                <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                                <div className="flex items-center space-x-1">
                                    {stat.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : stat.trend === 'down' ? (
                                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                                    ) : (
                                        <Target className="h-4 w-4 text-blue-500" />
                                    )}
                                    <p className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                                        stat.changeType === 'negative' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                        {stat.change} from last week
                                    </p>
                                </div>
                            </div>
                            <div className={`p-4 rounded-2xl ${stat.color} shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                                <stat.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Teacher Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {teacherMetrics.map((metric) => (
                    <Card key={metric.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{metric.name}</p>
                                <p className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
                                <div className="flex items-center space-x-1">
                                    {metric.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : metric.trend === 'down' ? (
                                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                                    ) : (
                                        <Target className="h-4 w-4 text-blue-500" />
                                    )}
                                    <p className={`text-sm font-medium ${metric.changeType === 'positive' ? 'text-green-600' :
                                        metric.changeType === 'negative' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                        {metric.change}
                                    </p>
                                </div>
                            </div>
                            <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                                <metric.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* My Classes */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">My Classes</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                            <BookOpen className="h-5 w-5 text-emerald-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {myClasses.map((classItem, index) => (
                            <div key={index} className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold text-gray-900">{classItem.subject}</h4>
                                    <span className="text-xs text-gray-500">{classItem.students} students</span>
                                </div>
                                <p className="text-xs text-gray-600 mb-2">{classItem.grade}</p>
                                <div className="flex items-center space-x-2">
                                    <Clock className="h-3 w-3 text-gray-500" />
                                    <span className="text-xs text-gray-500">Next: {classItem.nextClass}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Student Performance */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Student Performance</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                            <Star className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {studentPerformance.map((performance, index) => (
                            <div key={index} className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{performance.grade}</span>
                                    <span className="text-sm font-bold text-gray-900">{performance.average}%</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="text-xs text-gray-600 w-16">Average</div>
                                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                                        <div
                                            className={`${performance.color} h-3 rounded-full`}
                                            style={{ width: `${performance.average}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs font-medium text-green-600">{performance.improvement}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Activities */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100">
                            <Activity className="h-5 w-5 text-orange-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {recentActivities.map((activity) => (
                            <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-shrink-0">
                                    <div className={`w-3 h-3 rounded-full mt-2 ${activity.type === 'assignment' ? 'bg-emerald-500' :
                                        activity.type === 'attendance' ? 'bg-blue-500' :
                                            activity.type === 'exam' ? 'bg-orange-500' : 'bg-purple-500'
                                        }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Upcoming Tasks */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                            <Target className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {upcomingTasks.map((task, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{task.task}</p>
                                    <p className="text-xs text-gray-500">Due: {task.due}</p>
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>
                                    {task.priority}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card glassy={true} className="islamic-pattern">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <a
                        href="/attendance/mark"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Users className="h-6 w-6 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Mark Attendance</p>
                    </a>
                    <a
                        href="/assignments/create"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <FileText className="h-6 w-6 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Create Assignment</p>
                    </a>
                    <a
                        href="/grades/enter"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Award className="h-6 w-6 text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Enter Grades</p>
                    </a>
                    <a
                        href="/schedule"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Calendar className="h-6 w-6 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">View Schedule</p>
                    </a>
                </div>
            </Card>
        </div>
    );
};

export default DashboardTeacher;
